import { Component, Input, OnChanges, SimpleChanges, ChangeDetectorRef, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LiveShopService } from 'src/app/services/liveshop.service';
import { MessageService } from 'primeng/api';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';

interface PixelIntegration {
  key: string;
  name: string;
  description: string;
  category: string;
  logo: string;
  isPinned?: boolean;
}

@Component({
  selector: 'app-pixel-manager',
  templateUrl: './pixel-manager.component.html',
  styleUrls: ['./pixel-manager.component.scss']
})
export class PixelManagerComponent implements OnChanges {
  @Input() storeId?: string | null;

  pixelIntegrations: PixelIntegration[] = [
    {
      key: 'facebook',
      name: 'Facebook',
      description: 'Facebook',
      category: 'Pixel',
      logo: 'assets/icons/facebook.svg',
      isPinned: false
    }
  ];

  facebookPixel = false;
  addPixelDialog = false;
  filteredPixels: any[] = [];
  totalRecords = 0;
  loadingPixels = false;
  loadingPixelsTable = false;
  savePixelLoading = false;
  isEditing = false;
  pixel_id?: number;
  hasErrorNamePixel = false;
  hasErrorIdPixel = false;
  formData: any = this.createEmptyFormData();
  pixelForm?: FormGroup;

  constructor(
    private confirmationService: ConfirmationService,
    private liveShopService: LiveShopService,
    private messageService: MessageService,
    private errorHandler: ErrorHandlerService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private injector: Injector
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeId'] && this.facebookPixel) {
      this.loadPixelsLazy();
    }
  }

  openPixelDialog(_pixel: PixelIntegration): void {
    if (!this.storeId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Loja não encontrada',
        detail: 'Selecione uma loja para gerenciar os pixels.'
      });
      return;
    }
    this.facebookPixel = true;
    this.loadPixelsLazy();
  }

  loadPixelsLazy(event: any = {}): void {
    if (!this.facebookPixel || !this.storeId) {
      return;
    }

    this.loadingPixels = true;
    this.loadingPixelsTable = true;

    const page = ((event.first ?? 0) / (event.rows ?? 10)) || 0;
    const size = event.rows ?? 10;

    // Cache-first com ETag (src/app/core/http-cache).
    const pixelsResource = this.liveShopService.listPixelLazyRevalidated(this.storeId, page, size);

    toObservable(pixelsResource.data, { injector: this.injector }).pipe(
      filter((data): data is any => data !== null)
    ).subscribe(data => {
        if (data && data.data) {
          this.filteredPixels = data.data;
          this.totalRecords = data.total;
        } else {
          this.filteredPixels = [];
          this.totalRecords = 0;
        }
        this.loadingPixels = false;
        this.loadingPixelsTable = false;
        this.cdr.detectChanges();
    });

    toObservable(pixelsResource.error, { injector: this.injector }).pipe(
      filter((error): error is any => error !== null)
    ).subscribe(error => {
        this.loadingPixels = false;
        this.loadingPixelsTable = false;
        this.cdr.detectChanges();
        let message = '';
        this.savePixelLoading = false;

        if ([400, 401, 403].includes(error.status)) {
          message = error.error.message;
        } else if (error.status === 500) {
          message = 'Ops... Algo de errado aconteceu. Tente novamente mais tarde.';
        } else if (error.status !== 404) {
          message = error.error.message;
        }

        if (message) {
          this.showErrorDialog(message);
        }
    });
  }

  openAddPixel(): void {
    this.formData = this.createEmptyFormData();
    this.hasErrorNamePixel = false;
    this.hasErrorIdPixel = false;
    this.isEditing = false;
    this.addPixelDialog = true;
  }

  closeFacebookPixel(): void {
    this.facebookPixel = false;
  }

  closeAddFacebookPixel(): void {
    this.addPixelDialog = false;
    this.isEditing = false;
    this.pixel_id = undefined;
    this.loadPixelsLazy();
  }

  onDialogPixelClose(): void {
    this.addPixelDialog = false;
    this.hasErrorNamePixel = false;
    this.hasErrorIdPixel = false;
  }

  forceFocus(event: Event): void {
    (event.target as HTMLElement)?.focus();
  }

  savePixel(): void {
    this.validatePixelForm();
    if (this.hasErrorNamePixel || this.hasErrorIdPixel) {
      return;
    }

    if (!this.storeId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Loja não encontrada',
        detail: 'Selecione uma loja para salvar o pixel.'
      });
      return;
    }

    this.savePixelLoading = true;
    this.pixelForm = this.fb.group({
      key: this.formData.key,
      pixel_id: this.formData.pixel_id,
      access_token: this.formData.access_token,
      category: 'facebook',
      view_events: {
        fb_pixel_view: {
          view_events: this.formData.view_events.fb_pixel_view.view_events
        }
      },
      conversion_events: {
        fb_pixel_conversion: {
          conversion_events: this.formData.conversion_events.fb_pixel_conversion.conversion_events
        }
      }
    });

    this.liveShopService.create_pixel(this.pixelForm.value).subscribe({
      next: () => {
        this.savePixelLoading = false;
        this.addPixelDialog = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Pixel criado',
          detail: 'O pixel foi criado com sucesso!'
        });
        this.loadPixelsLazy();
      },
      error: (error) => {
        this.savePixelLoading = false;
        this.handleRequestError(error);
      }
    });
  }

  updatePixel(): void {
    this.validatePixelForm();
    if (this.hasErrorNamePixel || this.hasErrorIdPixel || !this.pixel_id) {
      return;
    }

    this.savePixelLoading = true;
    this.pixelForm = this.fb.group({
      key: this.formData.key,
      pixel_id: this.formData.pixel_id,
      access_token: this.formData.access_token,
      view_events: {
        fb_pixel_view: {
          view_events: this.formData.view_events.fb_pixel_view.view_events
        }
      },
      conversion_events: {
        fb_pixel_conversion: {
          conversion_events: this.formData.conversion_events.fb_pixel_conversion.conversion_events
        }
      }
    });

    this.liveShopService.update_pixel(this.pixelForm.value, this.pixel_id).subscribe({
      next: () => {
        this.savePixelLoading = false;
        this.addPixelDialog = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Pixel atualizado',
          detail: 'O pixel foi atualizado com sucesso!'
        });
        this.loadPixelsLazy();
      },
      error: (error) => {
        this.savePixelLoading = false;
        this.handleRequestError(error);
      }
    });
  }

  editPixel(pixel_id: any): void {
    this.pixel_id = pixel_id;

    // Cache-first com ETag (src/app/core/http-cache).
    const pixelResource = this.liveShopService.detailPixelRevalidated(pixel_id);

    toObservable(pixelResource.data, { injector: this.injector }).pipe(
      filter((resp): resp is any => resp !== null)
    ).subscribe(resp => {
        this.addPixelDialog = true;
        this.isEditing = true;
        this.formData = this.convertPixelDataToFormData(resp);
        this.cdr.detectChanges();
    });

    toObservable(pixelResource.error, { injector: this.injector }).pipe(
      filter((error): error is any => error !== null)
    ).subscribe(error => {
        this.savePixelLoading = false;
        this.handleRequestError(error);
    });
  }

  confirmDeletionPixel(pixel_id: any): void {
    this.facebookPixel = false;
    this.confirmationService.confirm({
      message: 'Você realmente deseja remover este pixel?',
      header: 'Confirmação de Exclusão',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deletarPixel(pixel_id);
      },
      reject: () => {
        this.facebookPixel = true;
      }
    });
  }

  deletarPixel(pixel_id: any): void {
    this.liveShopService.deletar_pixel(pixel_id).subscribe({
      next: () => {
        this.filteredPixels = this.filteredPixels.filter(pixel => pixel.id !== pixel_id);
        this.totalRecords = Math.max(this.totalRecords - 1, 0);
        this.facebookPixel = true;
        this.messageService.add({
          severity: 'success',
          summary: 'Pixel deletado',
          detail: 'O pixel foi deletado com sucesso!'
        });
      },
      error: (error) => {
        this.errorHandler.handle(error, 'Erro ao deletar pixel.');
      }
    });
  }

  private createEmptyFormData() {
    return {
      key: '',
      pixel_id: '',
      access_token: '',
      view_events: {
        fb_pixel_view: {
          view_events: {
            ss_view_percentage: false,
            ss_pitch_hit: false,
            ss_first_play: true,
            ss_product_view: false,
            ss_like: false,
            ss_cta_click: false,
            ss_redirect: false,
            ss_share: false
          }
        }
      },
      conversion_events: {
        fb_pixel_conversion: {
          conversion_events: {
            ss_add_to_cart: {
              enabled: false,         
              payload: {
                sku: '',
                title: '',
                category: '',
                qty: 0,
                price: 0
              }
            }
          }
        }
      }
    };
  }

  private convertPixelDataToFormData(pixelData: any) {
    const viewEvents =
      pixelData?.view_events?.fb_pixel_view?.view_events ||
      pixelData?.value_2?.view_events ||
      {};
    const conversionEvents =
      pixelData?.conversion_events?.fb_pixel_conversion?.conversion_events ||
      pixelData?.value_3?.conversion_events ||
      {};
    const addToCart =
      conversionEvents?.ss_add_to_cart ||
      conversionEvents?.add_to_cart || {
        enabled: false,
        payload: {
          sku: '',
          title: '',
          category: '',
          qty: 0,
          price: 0
        }
      };
    const normalizedAddToCart = {
      enabled: !!addToCart.enabled,
      payload: {
        sku: addToCart.payload?.sku || '',
        title: addToCart.payload?.title || '',
        category: addToCart.payload?.category || '',
        qty: addToCart.payload?.qty || 0,
        price: addToCart.payload?.price || 0
      }
    };
    const getFlag = (source: any, ...keys: string[]) => {
      for (const key of keys) {
        if (source && source[key] !== undefined) {
          return source[key];
        }
      }
      return false;
    };

    return {
      key: pixelData.key || '',
      pixel_id: pixelData.pixel_id || '',
      access_token: pixelData.access_token || '',
      view_events: {
        fb_pixel_view: {
          view_events: {
            ss_view_percentage: getFlag(viewEvents, 'ss_view_percentage', 'view_percentage'),
            ss_pitch_hit: getFlag(viewEvents, 'ss_pitch_hit', 'pitch_hit'),
            ss_first_play: true,
            ss_product_view: getFlag(viewEvents, 'ss_product_view', 'product_view'),
            ss_like: getFlag(viewEvents, 'ss_like', 'like'),
            ss_cta_click: getFlag(viewEvents, 'ss_cta_click', 'cta_click'),
            ss_redirect: getFlag(viewEvents, 'ss_redirect', 'redirect'),
            ss_share: getFlag(viewEvents, 'ss_share', 'share')
          }
        }
      },
      conversion_events: {
        fb_pixel_conversion: {
          conversion_events: {
            ss_add_to_cart: normalizedAddToCart
          }
        }
      }
    };
  }

  private validatePixelForm(): void {
    this.hasErrorNamePixel = !this.formData.key || this.formData.key.trim() === '';
    this.hasErrorIdPixel = !this.formData.pixel_id || this.formData.pixel_id.trim() === '';
  }

  private handleRequestError(error: any): void {
    let message = '';
    if ([400, 401, 403].includes(error.status)) {
      message = error.error.message;
    } else if (error.status === 500) {
      message = 'Ops... Algo de errado aconteceu. Tente novamente mais tarde.';
    } else if (error.status !== 404) {
      message = error.error.message;
    }

    if (message) {
      this.showErrorDialog(message);
    }
  }

  private showErrorDialog(message: string): void {
    this.confirmationService.confirm({
      message,
      header: 'Erro',
      icon: 'pi pi-times',
    });
  }
}
